import { Title } from "@src/lib/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib/Notifications";
import axios from "axios";
import { useState } from "react";
import { SettingsItem } from "./SettingsItem";
import { SessionType } from "@src/modules/interface";
import { useSelector } from "react-redux";
import { HCF_USER_EVENTS } from "@src/constants/firebaseEvents";
import { logEvent } from "@src/appV2/lib/analytics";
import { useUpdateDemoShiftOnTimeAttendanceOptOut } from "@src/appV2/FacilityProfile/api/useUpdateDemoShiftOnTimeAttendanceOptOut";
import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { FACILITY_HELP_SUPPORT_LINK } from "@src/constants/facility";

interface DemoShiftOnTimeAttendanceOptOutProps {
  workplace: WorkplaceProfileResponse | undefined;
  onPreferencesChanged: () => void;
}

export function DemoShiftOnTimeAttendanceOptOut(props: DemoShiftOnTimeAttendanceOptOutProps) {
  const { workplace, onPreferencesChanged } = props;

  const isDemoShiftOnTimeAttendanceOptOutEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_FACILITY_DEMO_SHIFT_ON_TIME_ATTENDANCE_OPT_OUT,
    { defaultValue: false }
  );

  const { showSuccessToast, showErrorToast } = useToast();
  const [selectedToggle, setSelectedToggle] = useState<boolean>(
    workplace?.data.attributes.demoShift?.onTimeAttendanceOptOut ?? false
  );
  const { user, admin } = useSelector((state: SessionType) => state.session);
  const { isLoading, mutateAsync: updateOptOut } = useUpdateDemoShiftOnTimeAttendanceOptOut();

  if (!isDemoShiftOnTimeAttendanceOptOutEnabled) {
    return null;
  }

  // Only show if the setting exists in the workplace attributes
  if (!admin || !workplace?.data.attributes.demoShift) {
    return null;
  }

  const updateEnabled = async (enabled: boolean) => {
    const workplaceId = workplace!.data.id;

    if (isDefined(user)) {
      logEvent(HCF_USER_EVENTS.DEMO_SHIFT_ON_TIME_ATTENDANCE_OPTOUT_TOGGLED, {
        workplaceId,
        workplaceUserId: user._id,
        value: enabled,
      });
    }

    try {
      await updateOptOut({ workplaceId, enabled });

      showSuccessToast("Ignore Demo Shift requirement updated successfully");
      onPreferencesChanged();
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && isDefined(error.response)) {
        showErrorToast(error.response.data);
      } else {
        showErrorToast("Error updating preferences. Please, try again later or contact support");
      }
    }
  };

  function getSubtitle() {
    return (
      <>
        Let professionals who haven't shown up on time to the Demo Shift and completed it book
        shifts at my workplace.{" "}
        <a href={FACILITY_HELP_SUPPORT_LINK} target="_blank">
          Learn more
        </a>
      </>
    );
  }

  return (
    <Stack position="relative" pl={2} sx={{ opacity: isLoading ? 0.5 : 1 }}>
      <Title component="h3">Demo Shift</Title>

      {isLoading ? (
        <Box position="absolute" top="calc(50% - 24px)" left="calc(50% - 24px)">
          <CircularProgress size={48} />
        </Box>
      ) : null}

      <SettingsItem
        item={{
          key: "demoShiftOnTimeAttendanceOptOut",
          title: "Ignore Demo Shift requirement",
          subtitle: getSubtitle(),
          checked: selectedToggle,
          onChange: (checked: boolean) => {
            void updateEnabled(checked);
            setSelectedToggle(checked);
          },
          loading: isLoading,
        }}
        isLoading={isLoading}
      />
    </Stack>
  );
}
